import { useState, useEffect, useRef } from "react";
import { Navigate, NavLink, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { clearToken, getSession, getToken, SessionUser, UnauthorizedError, UserRole } from "./api";
import Login from "./pages/Login";
import Studio from "./pages/Studio";
import Templates from "./pages/Templates";
import Categories from "./pages/Categories";
import Records from "./pages/Records";

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  roles?: UserRole[];
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "生成台", end: true },
  { to: "/templates", label: "模板管理", roles: ["admin"] },
  { to: "/categories", label: "分类管理", roles: ["admin"] },
  { to: "/records", label: "生成记录" },
];

interface LegacyMediaQueryList {
  addListener: (listener: (e: MediaQueryListEvent) => void) => void;
  removeListener: (listener: (e: MediaQueryListEvent) => void) => void;
}

function Shell({ children, role }: { children: React.ReactNode; role: UserRole }) {
  const visibleNavItems = NAV_ITEMS.filter((item) => !item.roles || item.roles.includes(role));
  const nav = useNavigate();
  const location = useLocation();
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  const rafId1 = useRef<number | null>(null);
  const rafId2 = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const afterCloseRef = useRef<(() => void) | null>(null);

  const link = "px-3 py-2 rounded-md text-sm font-medium hover:bg-slate-100";
  const active = "bg-teal-50 text-teal-700";

  // Find the active nav item label for the mobile header
  const currentNavItem = visibleNavItems.find((item) => {
    if (item.end) {
      return location.pathname === item.to;
    }
    return location.pathname.startsWith(item.to);
  });
  const currentTitle = currentNavItem ? currentNavItem.label : "";

  const handleOpen = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    afterCloseRef.current = null;
    setIsMounted(true);
    
    if (rafId1.current) cancelAnimationFrame(rafId1.current);
    if (rafId2.current) cancelAnimationFrame(rafId2.current);
    
    rafId1.current = requestAnimationFrame(() => {
      rafId2.current = requestAnimationFrame(() => {
        setIsVisible(true);
      });
    });
  };

  const handleClose = (callback?: () => void) => {
    setIsVisible(false);
    
    if (rafId1.current) {
      cancelAnimationFrame(rafId1.current);
      rafId1.current = null;
    }
    if (rafId2.current) {
      cancelAnimationFrame(rafId2.current);
      rafId2.current = null;
    }
    clearTimeout(closeTimerRef.current ?? undefined);
    closeTimerRef.current = null;
    
    afterCloseRef.current = callback || null;
    
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const delay = prefersReducedMotion ? 0 : 300;
    
    closeTimerRef.current = window.setTimeout(() => {
      setIsMounted(false);
      closeTimerRef.current = null;
      if (afterCloseRef.current) {
        afterCloseRef.current();
        afterCloseRef.current = null;
      }
    }, delay);
  };

  // Helper for mobile logout
  const handleLogout = () => {
    handleClose(() => {
      clearToken();
      nav("/login", { replace: true });
    });
  };

  // Clear RAF/timers on unmount
  useEffect(() => {
    return () => {
      if (rafId1.current) cancelAnimationFrame(rafId1.current);
      if (rafId2.current) cancelAnimationFrame(rafId2.current);
      clearTimeout(closeTimerRef.current ?? undefined);
    };
  }, []);

  // Escape key, Tab containment, Scroll lock, Focus restoration
  useEffect(() => {
    if (!isMounted) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const timer = setTimeout(() => {
      if (drawerRef.current) {
        const selector = 'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])';
        const elements = drawerRef.current.querySelectorAll<HTMLElement>(selector);
        const focusables = Array.from(elements).filter((el) => {
          const isNotDisabled = !el.hasAttribute("disabled");
          const isNotHidden = el.getAttribute("aria-hidden") !== "true";
          return isNotDisabled && isNotHidden;
        });
        if (focusables.length > 0) {
          focusables[0].focus();
        }
      }
    }, 0);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
        return;
      }

      if (e.key === "Tab") {
        if (!drawerRef.current) return;
        const selector = 'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])';
        const elements = drawerRef.current.querySelectorAll<HTMLElement>(selector);
        const focusables = Array.from(elements).filter((el) => {
          const isNotDisabled = !el.hasAttribute("disabled");
          const isNotHidden = el.getAttribute("aria-hidden") !== "true";
          return isNotDisabled && isNotHidden;
        });
        if (focusables.length === 0) return;

        const firstEl = focusables[0];
        const lastEl = focusables[focusables.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            lastEl.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastEl) {
            firstEl.focus();
            e.preventDefault();
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      menuButtonRef.current?.focus();
    };
  }, [isMounted]);

  // matchMedia listener to close drawer immediately when transitioning to desktop >=640px
  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 640px)");
    
    const handleMediaChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        if (rafId1.current) cancelAnimationFrame(rafId1.current);
        if (rafId2.current) cancelAnimationFrame(rafId2.current);
        clearTimeout(closeTimerRef.current ?? undefined);
        closeTimerRef.current = null;
        afterCloseRef.current = null;
        setIsVisible(false);
        setIsMounted(false);
      }
    };

    if ("addEventListener" in mediaQuery) {
      mediaQuery.addEventListener("change", handleMediaChange);
    } else if ("addListener" in mediaQuery) {
      (mediaQuery as unknown as LegacyMediaQueryList).addListener(handleMediaChange);
    }

    if (mediaQuery.matches) {
      if (rafId1.current) cancelAnimationFrame(rafId1.current);
      if (rafId2.current) cancelAnimationFrame(rafId2.current);
      clearTimeout(closeTimerRef.current ?? undefined);
      closeTimerRef.current = null;
      afterCloseRef.current = null;
      setIsVisible(false);
      setIsMounted(false);
    }

    return () => {
      if ("removeEventListener" in mediaQuery) {
        mediaQuery.removeEventListener("change", handleMediaChange);
      } else if ("removeListener" in mediaQuery) {
        (mediaQuery as unknown as LegacyMediaQueryList).removeListener(handleMediaChange);
      }
    };
  }, []);

  return (
    <div className="min-h-full">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 h-14 flex items-center justify-between sm:justify-start gap-2">
          {/* Mobile breadcrumb-style header title */}
          <div className="flex sm:hidden items-center text-sm font-medium text-slate-800 min-w-0">
            <span className="font-bold text-slate-800 shrink-0 mr-1">朵云换脸</span>
            <span className="text-slate-400 mx-1 shrink-0">/</span>
            <span className="text-slate-600 truncate">{currentTitle}</span>
          </div>

          {/* Desktop Brand */}
          <div className="hidden sm:block font-bold text-slate-800 shrink-0 mr-1 sm:mr-4">朵云换脸</div>

          {/* Desktop Navigation */}
          <nav className="hidden sm:flex items-center gap-1 sm:gap-2 overflow-x-auto flex-1 min-w-0 [&::-webkit-scrollbar]:hidden">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `${link} whitespace-nowrap ${isActive ? active : "text-slate-600"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Desktop Logout Button */}
          <button
            type="button"
            onClick={() => {
              clearToken();
              nav("/login", { replace: true });
            }}
            className="hidden sm:block px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700 shrink-0"
          >
            退出
          </button>

          {/* Mobile Hamburger Button */}
          <button
            ref={menuButtonRef}
            type="button"
            onClick={handleOpen}
            aria-expanded={isVisible}
            aria-controls="mobile-navigation-drawer"
            aria-label="打开菜单"
            className="flex sm:hidden items-center justify-center w-11 h-11 rounded-md text-slate-600 hover:text-slate-800 hover:bg-slate-100 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
          >
            <svg aria-hidden="true" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Drawer (Sibling after header, outside sticky header stacking context) */}
      {isMounted && (
        <div
          id="mobile-navigation-drawer"
          ref={drawerRef}
          className="fixed inset-0 z-50 flex justify-end sm:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="导航菜单"
        >
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300 motion-reduce:transition-none ${
              isVisible ? "opacity-100 ease-out" : "opacity-0 ease-in"
            }`}
            onClick={() => handleClose()}
            aria-hidden="true"
          />

          {/* Drawer Panel */}
          <div
            className={`relative w-4/5 max-w-sm h-full bg-white shadow-xl flex flex-col justify-between overflow-y-auto transform transition-transform duration-300 motion-reduce:transition-none ${
              isVisible ? "translate-x-0 ease-out" : "translate-x-full ease-in"
            }`}
            style={{
              paddingTop: "calc(1rem + env(safe-area-inset-top))",
              paddingBottom: "calc(1rem + env(safe-area-inset-bottom))",
              paddingLeft: "calc(1rem + env(safe-area-inset-left))",
              paddingRight: "calc(1rem + env(safe-area-inset-right))",
            }}
          >
            <div>
              <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-4">
                <span className="font-bold text-slate-800 text-base">导航菜单</span>
                <button
                  type="button"
                  onClick={() => handleClose()}
                  aria-label="关闭菜单"
                  className="flex items-center justify-center w-11 h-11 rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                >
                  <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Navigation Links inside Drawer */}
              <nav aria-label="移动导航" className="flex flex-col gap-2">
                {visibleNavItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={(e) => {
                      if (e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) {
                        return;
                      }
                      e.preventDefault();
                      handleClose(() => nav(item.to));
                    }}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-lg text-base font-medium transition-colors motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${
                        isActive ? "bg-teal-50 text-teal-700 font-semibold" : "text-slate-600 hover:bg-slate-50"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>

            {/* Logout section */}
            <div className="pt-4 border-t border-slate-100 mt-auto">
              <button
                type="button"
                onClick={handleLogout}
                className="w-full px-4 py-3 rounded-lg text-base font-medium text-red-600 hover:bg-red-50 transition-colors motion-reduce:transition-none flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
              >
                <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                退出登录
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">{children}</main>
    </div>
  );
}

function ProtectedApp() {
  const [session, setSession] = useState<SessionUser | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "unauthenticated" | "failed">(
    getToken() ? "loading" : "unauthenticated",
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!getToken()) return;
    let active = true;
    setState("loading");
    getSession()
      .then(({ user }) => {
        if (!active) return;
        setSession(user);
        setState("ready");
      })
      .catch((error: unknown) => {
        if (!active) return;
        if (error instanceof UnauthorizedError || !getToken()) {
          setState("unauthenticated");
          return;
        }
        setState("failed");
      });
    return () => {
      active = false;
    };
  }, [attempt]);

  if (state === "unauthenticated") return <Navigate to="/login" replace />;
  if (state === "loading") return <div className="min-h-full flex items-center justify-center p-4 text-sm text-slate-500">正在验证登录状态…</div>;
  if (state === "failed" || !session) {
    return (
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="w-full max-w-sm bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4">
          <div className="text-sm text-slate-600">无法验证登录状态，请检查网络后重试。</div>
          <button type="button" onClick={() => setAttempt((value) => value + 1)} className="w-full px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">重试</button>
        </div>
      </div>
    );
  }

  const shell = (page: React.ReactNode) => <Shell role={session.role}>{page}</Shell>;
  const isAdmin = session.role === "admin";
  return (
    <Routes>
      <Route path="/" element={shell(<Studio />)} />
      <Route path="/templates" element={isAdmin ? shell(<Templates />) : <Navigate to="/" replace />} />
      <Route path="/categories" element={isAdmin ? shell(<Categories />) : <Navigate to="/" replace />} />
      <Route path="/records" element={shell(<Records />)} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="*" element={<ProtectedApp />} />
    </Routes>
  );
}